import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LanguageIcon from '@mui/icons-material/Language';
import InstagramIcon from '@mui/icons-material/Instagram';
import ShareIcon from '@material-ui/icons/Share';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { mediaKitLandingPath } from '../../../../data/staticLandingSlugs';

const DEFAULT_TITLE = 'Nuestros Números';
const DEFAULT_DESCRIPTION =
  'Consolidamos tu identidad de marca ante una audiencia internacional mediante desarrollos digitales innovadores.';

const DEFAULT_METRICS = [
  {
    value: 30000,
    suffix: '+',
    label: 'Visitas mensuales en plataforma web y editorial',
    subtitle: 'Alcance constante y crecimiento sostenido.',
    accent: '#1565c0',
    Icon: LanguageIcon,
  },
  {
    value: 400000,
    suffix: '+',
    label: 'Cuentas alcanzadas en Instagram',
    subtitle: 'Visualizaciones de alcance en la cuenta de TNO Network al cierre de mayo.',
    accent: '#c2185b',
    Icon: InstagramIcon,
  },
  {
    value: 90000,
    suffix: '+',
    label: 'Seguidores totales en ecosistema de redes sociales',
    subtitle: 'Instagram, X y YouTube combinados.',
    accent: '#e91e63',
    Icon: ShareIcon,
  },
  {
    value: 40,
    suffix: '+',
    label: 'Producciones originales On-Demand',
    subtitle: 'Desarrolladas en el Media Lab.',
    accent: '#6a1b9a',
    Icon: OndemandVideoIcon,
  },
  {
    value: 25,
    suffix: '+',
    label: 'Programas en vivo semanalmente',
    subtitle: 'Contenido fresco y constante.',
    accent: '#00838f',
    Icon: LiveTvIcon,
  },
];

const ICON_MAP = {
  LanguageIcon: LanguageIcon,
  InstagramIcon,
  TwitterIcon: ShareIcon,
  YouTubeIcon: ShareIcon,
  OndemandVideoIcon: OndemandVideoIcon,
  LiveTvIcon: LiveTvIcon,
};

function parseMetricValue(metric) {
  if (typeof metric.title === 'number' && !Number.isNaN(metric.title)) return metric.title;
  if (metric.value != null && metric.value !== '') {
    const n = parseInt(String(metric.value).replace(/\D/g, ''), 10);
    if (!Number.isNaN(n)) return n;
  }
  if (metric.title != null && metric.title !== '') {
    const n = parseInt(String(metric.title).replace(/\D/g, ''), 10);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

function normalizeMetricsFromApi(metrics) {
  const accents = ['#1565c0', '#c2185b', '#e91e63', '#6a1b9a', '#00838f'];
  const defaultIcons = [LanguageIcon, InstagramIcon, ShareIcon, OndemandVideoIcon, LiveTvIcon];
  return metrics.map((metric, i) => {
    const IconComponent = ICON_MAP[metric.icon] || defaultIcons[i % defaultIcons.length];
    const secondaryValue =
      metric.secondaryValue != null && metric.secondaryValue !== ''
        ? parseMetricValue({ value: metric.secondaryValue })
        : null;
    return {
      value: parseMetricValue(metric),
      suffix: metric.suffix != null ? metric.suffix : '+',
      label: metric.label || '',
      subtitle: metric.subtitle || '',
      secondaryValue,
      secondarySuffix: metric.secondarySuffix != null ? metric.secondarySuffix : '+',
      secondaryLabel: metric.secondaryLabel || '',
      href: metric.href || (metric.mediaKitSlug ? mediaKitLandingPath(metric.mediaKitSlug) : ''),
      linkLabel: metric.linkLabel || 'Ver media kit',
      accent: accents[i % accents.length],
      Icon: IconComponent,
    };
  });
}

function MetricCard({ item, animate }) {
  const { value, suffix, label, subtitle, secondaryValue, secondarySuffix, secondaryLabel, href, linkLabel, accent, Icon } =
    item;

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 3,
        p: { xs: 2.25, md: 2.75 },
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(accent, 0.2)}`,
        borderLeft: `4px solid ${accent}`,
        boxShadow: `0 12px 32px ${alpha(accent, 0.1)}`,
        background: `linear-gradient(145deg, ${alpha(accent, 0.06)} 0%, transparent 55%)`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 18px 40px ${alpha(accent, 0.16)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: 26 }} />
      </Box>
      {value > 0 && (
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: '2.35rem', md: '2.75rem' },
            lineHeight: 1,
            letterSpacing: '-0.03em',
            color: accent,
            mb: 1.25,
          }}
        >
          <CountUp end={animate ? value : 0} duration={2} separator="." suffix={suffix || ''} />
        </Typography>
      )}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: value > 0 ? '0.98rem' : { xs: '1.65rem', md: '1.9rem' },
          lineHeight: 1.35,
          mb: subtitle ? 0.5 : 0.75,
          color: value > 0 ? 'text.primary' : accent,
        }}
      >
        {label}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.5, mb: secondaryValue != null ? 1.5 : 0 }}>
          {subtitle}
        </Typography>
      )}
      {secondaryValue != null && (
        <>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.65rem', md: '1.9rem' },
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: accent,
              mb: 0.5,
              opacity: 0.92,
            }}
          >
            <CountUp
              end={animate ? secondaryValue : 0}
              duration={2}
              separator="."
              suffix={secondarySuffix || ''}
            />
          </Typography>
          {secondaryLabel && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.5, mb: href ? 1.25 : 0 }}>
              {secondaryLabel}
            </Typography>
          )}
        </>
      )}
      {href && (
        <Typography
          component={RouterLink}
          to={href}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: secondaryValue != null ? 0 : 1,
            fontWeight: 700,
            fontSize: '0.85rem',
            color: accent,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {linkLabel}
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Typography>
      )}
    </Box>
  );
}

MetricCard.propTypes = {
  item: PropTypes.shape({
    value: PropTypes.number,
    suffix: PropTypes.string,
    label: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    secondaryValue: PropTypes.number,
    secondarySuffix: PropTypes.string,
    secondaryLabel: PropTypes.string,
    href: PropTypes.string,
    linkLabel: PropTypes.string,
    accent: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
  }).isRequired,
  animate: PropTypes.bool,
};

const Metrics = ({ metrics = [], content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const [viewPortEntered, setViewPortEntered] = useState(false);

  const metricsData =
    metrics.length > 0 ? normalizeMetricsFromApi(metrics) : DEFAULT_METRICS;

  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;

  return (
    <VisibilitySensor
      partialVisibility
      onChange={(isVisible) => {
        if (isVisible) setViewPortEntered(true);
      }}
    >
      <Box
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          background: `linear-gradient(165deg, ${alpha(primary, 0.05)} 0%, ${alpha(secondary, 0.08)} 50%, transparent 100%)`,
          px: { xs: 1, md: 2 },
          py: { xs: 3, md: 5 },
          my: { xs: 2, md: 3 },
        }}
      >
        <Box textAlign="center" mb={{ xs: 3, md: 4 }} maxWidth={720} mx="auto">
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
            color="secondary"
            gutterBottom
          >
            {content.overline || 'Indicadores clave'}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              letterSpacing: '-0.02em',
              color: 'primary.main',
              mb: 1.5,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem', lineHeight: 1.7 }}>{description}</Typography>
        </Box>

        <Grid container spacing={2.5}>
          {metricsData.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.label}>
              <MetricCard item={item} animate={viewPortEntered} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </VisibilitySensor>
  );
};

Metrics.propTypes = {
  metrics: PropTypes.arrayOf(PropTypes.object),
  content: PropTypes.shape({
    overline: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

Metrics.defaultProps = {
  metrics: [],
  content: {},
};

export default Metrics;
